import { loadApplicationStatus, submitLeaseApplication } from 'api/services/applicationService'
import useAuthService from 'api/services/authService'
import {
  createVisitRequest,
  loadListingVisitSlots,
} from 'api/services/ownerListingManagerService'
import usePropertyService from 'api/services/propertyService'
import { loadMyVerification } from 'api/services/verificationService'
import { getAuthUserSubject } from 'api/utils/AuthManager'
import { setPendingApplyListingId } from 'api/utils/pendingApplyListing'
import PageLoading from 'components/common/loading/PageLoading'
import { parseFloorFromApi } from 'components/createAd/createAdFlowState'
import ListingPhotoGalleryModal from 'components/createAd/ListingPhotoGalleryModal'
import { buildStructuredListingTitle } from 'components/listing/buildStructuredListingTitle'
import InterestModal, { InterestFormValues } from 'components/listing/InterestModal'
import ListingVerificationBadge from 'components/listings/ListingVerificationBadge'
import { applicationStatusKey } from 'components/myAccount/mySpaceUtils'
import { formatListingAvailabilityLabel } from 'components/propertyCard/formatListingAvailability'
import { formatListingPrice } from 'components/propertyCard/formatListingPrice'
import {
  listingDisplayAddressLine,
} from 'components/propertyCard/listingAddressLine'
import {
  listingRoomLabel,
  listingTypeLabel,
  listingUnitTypeLabel,
} from 'components/propertyCard/listingCardMeta'
import PropertyCard from 'components/propertyCard/PropertyCard'
import PropertySharePopup from 'components/propertyCard/PropertySharePopup'
import useListingFavorite from 'components/propertyCard/useListingFavorite'
import { formatDate } from 'components/utils/FormatDate'
import SecurityVerificationModal from 'components/verification/SecurityVerificationModal'
import { submitTenantVerificationDocuments } from 'components/verification/submitTenantVerification'
import { canProceedAfterTenantVerification } from 'components/verification/verificationUtils'
import { Unit } from 'newTypes/unit'
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { ListedProperty, VerificationStatus } from 'types/property'
import { ApplicationPipelineStatus } from 'types/workflowStatus'
import {
  formatLocalizedDate,
  moneyFormatLocale,
  normalizeLangCode,
  tFloorLabel,
} from 'utils/i18nLocale'
import { scrollAppToTop } from 'utils/scrollAppToTop'

import { i18n as appI18n } from '../i18n'

const DESCRIPTION_PREVIEW_LENGTH = 320

/** User still needs to upload / correct identity documents. */
const INTEREST_ACTION_STATUSES: ApplicationPipelineStatus[] = [
  'started',
  'identity_required',
  'correction_requested',
]

/** Documents submitted; waiting for support review. */
const INTEREST_WAITING_STATUSES: ApplicationPipelineStatus[] = ['documents_in_review']

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm shadow-sm transition-shadow hover:shadow-md">
      <div className="mb-1 text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className="break-words font-semibold text-imovSecondary [overflow-wrap:anywhere]">
        {value}
      </div>
    </div>
  )
}

function FeaturePill({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex max-w-full items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-700 [overflow-wrap:anywhere]">
      {children}
    </span>
  )
}

function PhotoTile({
  src,
  onClick,
  className,
  children,
  ariaLabel,
}: {
  src: string
  onClick: () => void
  className?: string
  children?: ReactNode
  ariaLabel?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={`group relative block w-full overflow-hidden rounded-xl bg-gray-100 text-left transition-transform duration-200 hover:brightness-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-imovPrimary ${className ?? ''}`}
    >
      <img
        src={src}
        alt=""
        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
      />
      {children}
    </button>
  )
}

const PropertyPage = () => {
  const { t, i18n } = useTranslation()
  const lang = normalizeLangCode(i18n.language)
  const moneyLocale = moneyFormatLocale(lang)
  const navigate = useNavigate()
  const location = useLocation()
  const listingReturnTo =
    (location.state as { returnTo?: string } | null)?.returnTo ?? '/listings'
  const queryClient = useQueryClient()
  const { id } = useParams()
  const { loadListedProperty, loadListedProperties, loadMyPropertyById } =
    usePropertyService()
  const { isAuthenticated, user } = useAuthService()
  const [listing, setListing] = useState<Unit | null>(null)
  const [galleryOpen, setGalleryOpen] = useState(false)
  const [galleryIndex, setGalleryIndex] = useState(0)
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)
  const [isShareOpen, setIsShareOpen] = useState(false)
  const shareAnchorRef = useRef<HTMLDivElement | null>(null)
  const [showVerificationModal, setShowVerificationModal] = useState(false)
  const [showInterestModal, setShowInterestModal] = useState(false)
  const [pendingVisitSlotId, setPendingVisitSlotId] = useState<number | null>(null)
  const [isSubmittingVerification, setIsSubmittingVerification] = useState(false)
  const [isSubmittingInterest, setIsSubmittingInterest] = useState(false)

  const { data: propertyData, isLoading } = useQuery(
    ['loadProperty', id],
    () => loadListedProperty(id!),
    { enabled: !!id }
  )

  const listingCity =
    (propertyData as Unit | undefined)?.address?.city?.trim() || ''

  const { data: similarRaw = [] } = useQuery(
    ['similarListings', id, listingCity],
    () =>
      loadListedProperties({
        location: { city: listingCity },
      }),
    { enabled: !!id && listingCity !== '' }
  )

  const { data: myListingAsOwner } = useQuery(
    ['myListingOwnership', id],
    () => loadMyPropertyById(id!),
    { enabled: !!id && isAuthenticated }
  )

  const isOwner = useMemo(() => {
    if (!isAuthenticated) return false
    if (myListingAsOwner) return true
    if (!propertyData) return false
    const uid = getAuthUserSubject(user)
    if (!uid) return false
    const ownerKey = (propertyData as Unit).userFk
    if (ownerKey === undefined || ownerKey === null) return false
    return String(ownerKey) === String(uid)
  }, [isAuthenticated, myListingAsOwner, propertyData, user])

  useEffect(() => {
    if (propertyData) {
      setListing(propertyData as Unit)
    }
  }, [propertyData])

  useEffect(() => {
    setGalleryOpen(false)
    setGalleryIndex(0)
    setDescriptionExpanded(false)
  }, [id])

  const favoriteProperties = useMemo(() => {
    if (!listing) return [] as ListedProperty[]
    return [
      {
        id: String(listing.id),
        price: String(listing.price ?? ''),
        currency: listing.currency || 'CAD',
        verified: listing.verified,
        isFavorited: Boolean(listing.isFavorited),
        bedrooms: Number(listing.bedrooms) || 0,
        bathrooms: Number(listing.bathrooms) || 0,
        size: Number(listing.size) || 0,
        images: listing.images || [],
        location: listing.address?.city || '',
        availability: listing.availability,
        contractEnd: listing.contractEnd,
      } satisfies ListedProperty,
    ]
  }, [listing])

  const { handleFavorite, loadingFavoriteId } = useListingFavorite({
    properties: favoriteProperties,
    onToggleFavorite: (propertyId, isFavorited) => {
      if (String(propertyId) !== String(listing?.id)) return
      setListing(prev => (prev ? { ...prev, isFavorited } : prev))
    },
  })

  const similarListings = useMemo(() => {
    const rows = Array.isArray(similarRaw) ? similarRaw : []
    return (rows as ListedProperty[])
      .filter(item => String(item.id) !== String(id))
      .slice(0, 3)
  }, [similarRaw, id])

  useLayoutEffect(() => {
    scrollAppToTop()
  }, [id])

  useLayoutEffect(() => {
    if (!isLoading && propertyData) {
      scrollAppToTop()
    }
  }, [id, isLoading, propertyData])

  const { data: applicationStatus, isLoading: isApplicationStatusLoading } = useQuery(
    ['applicationStatus', id],
    () => loadApplicationStatus(id!),
    { enabled: !!id && isAuthenticated && !!propertyData && !isOwner }
  )

  const { data: identityVerification } = useQuery(
    ['myVerification'],
    loadMyVerification,
    { enabled: isAuthenticated && !isOwner }
  )

  const { data: visitSlots = [] } = useQuery(
    ['listingVisitSlots', id],
    () => loadListingVisitSlots(id!),
    { enabled: !!id && isAuthenticated && !isOwner }
  )

  const visitRequestMutation = useMutation(createVisitRequest)

  const applicationPipelineStatus = applicationStatusKey(
    applicationStatus?.application?.application_status
  )
  const hasApplied = Boolean(applicationStatus?.hasApplied)
  const identityStatus = identityVerification?.status
  const identityInReview = identityStatus === 'identity_in_review'
  const needsVerificationAction =
    hasApplied &&
    !identityInReview &&
    !canProceedAfterTenantVerification(identityVerification) &&
    (INTEREST_ACTION_STATUSES.includes(applicationPipelineStatus) ||
      identityStatus === 'profile_incomplete' ||
      identityStatus === 'identity_correction_requested')
  const isAwaitingValidation =
    hasApplied &&
    (INTEREST_WAITING_STATUSES.includes(applicationPipelineStatus) || identityInReview)
  const isInterestOnStandby = needsVerificationAction || isAwaitingValidation

  useEffect(() => {
    const resumeInterest = Boolean(
      (location.state as { resumeInterest?: boolean } | null)?.resumeInterest
    )
    if (!resumeInterest || !isAuthenticated || isOwner || !propertyData) return
    if (isApplicationStatusLoading) return
    if (hasApplied) {
      navigate(location.pathname, { replace: true, state: {} })
      return
    }
    setShowInterestModal(true)
    navigate(location.pathname, { replace: true, state: {} })
  }, [
    location.state,
    location.pathname,
    isAuthenticated,
    isOwner,
    hasApplied,
    isApplicationStatusLoading,
    propertyData,
    navigate,
  ])

  const meta = listing?.listingMeta

  const includes = useMemo(() => {
    if (!listing) return []
    const set = new Set<string>()
    const services = meta?.servicesIncluded
    if (Array.isArray(services)) {
      services.forEach(key => {
        if (key === 'electromenagers') return
        set.add(t(`createAd.flow.services.${key}`, { defaultValue: key }))
      })
    }
    const appliancesMode = meta?.electromenagersMode
    if (appliancesMode === 'included') {
      set.add(t('createAd.flow.electromenagers.includedLabel'))
    } else if (appliancesMode === 'paid') {
      const paidNote =
        typeof meta?.electromenagersPaidNote === 'string'
          ? meta.electromenagersPaidNote.trim()
          : ''
      set.add(
        paidNote
          ? `${t('createAd.flow.electromenagers.paidLabel')}: ${paidNote}`
          : t('createAd.flow.electromenagers.paidLabel')
      )
    }
    if (listing.details.heat) set.add(t('listing.detail.legacyHeat'))
    if (listing.details.electricity) set.add(t('listing.detail.legacyElectricity'))
    if (listing.details.internet) set.add(t('listing.detail.legacyInternet'))
    if (listing.details.parking) set.add(t('listing.detail.legacyParking'))
    if (listing.details.snowRemoval) set.add(t('listing.detail.legacySnow'))
    if (listing.details.furnished) set.add(t('listing.detail.legacyFurnished'))
    if (listing.details.semiFurnished && !listing.details.furnished) {
      set.add(t('listing.detail.legacySemiFurnished'))
    }
    return Array.from(set)
  }, [listing, meta?.servicesIncluded, meta?.electromenagersMode, t])

  const buildingAmenityLabels = useMemo(() => {
    const keys = meta?.buildingAmenities
    if (!Array.isArray(keys)) return []
    return keys.map(k => t(`createAd.flow.amenities.${k}`, { defaultValue: k })).filter(Boolean)
  }, [meta?.buildingAmenities, t])

  const pets = useMemo(() => {
    if (!listing) return { line: '—' as string, detail: undefined as string | undefined }
    const code = meta?.petsAllowed
    const note = listing.petsConditionsNote ?? meta?.petsConditionsNote ?? undefined
    if (code === 'yes') return { line: t('createAd.flow.pets.yes'), detail: note?.trim() || undefined }
    if (code === 'no') return { line: t('createAd.flow.pets.no'), detail: undefined }
    if (code === 'conditions') {
      return {
        line: t('createAd.flow.publication.preview.petsConditions'),
        detail: note?.trim() || undefined,
      }
    }
    if (listing.details.petFriendly) {
      return { line: t('listing.detail.petsYesPending'), detail: undefined }
    }
    return { line: t('listing.detail.petsNo'), detail: undefined }
  }, [listing, meta?.petsAllowed, meta?.petsConditionsNote, listing?.petsConditionsNote, t])

  if (!listing || isLoading) {
    return <PageLoading />
  }

  const goToAuthForInterest = () => {
    if (!id) return
    setPendingApplyListingId(id)
    navigate('/auth', {
      state: {
        applyListingId: id,
        from: location,
        returnTo: `/listing/${id}`,
      },
    })
  }

  const handleExpressInterest = () => {
    if (!id) return
    if (!getAuthUserSubject(user)) {
      goToAuthForInterest()
      return
    }
    setShowInterestModal(true)
  }

  const handleContinueVerification = () => {
    if (!getAuthUserSubject(user)) {
      goToAuthForInterest()
      return
    }
    setPendingVisitSlotId(null)
    setShowVerificationModal(true)
  }

  const handleInterestSubmit = async (values: InterestFormValues) => {
    if (!id) return
    setIsSubmittingInterest(true)
    try {
      const result = await submitLeaseApplication({
        propertyId: id,
        source: 'interest',
        moveInDate: values.moveInDate,
        incomeRange: 'à préciser',
        occupants: 1,
        hasPets: 'no',
        acceptCreditCheck: values.acceptCreditCheck,
        message: values.message || undefined,
      })
      await queryClient.invalidateQueries(['applicationStatus', id])
      setShowInterestModal(false)

      if (result.data?.requiresVerification || !canProceedAfterTenantVerification(identityVerification)) {
        toast.success(String(t('listing.interest.standbySuccess')))
        setShowVerificationModal(true)
        return
      }

      toast.success(String(t('listing.interest.confirmedSuccess')))
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : t('listing.interest.submitFailed')
      toast.error(String(msg))
    } finally {
      setIsSubmittingInterest(false)
    }
  }

  const bookVisit = async (slotId: number) => {
    if (!id) return
    try {
      await visitRequestMutation.mutateAsync({ listingId: id, slotId })
      toast.success(String(t('verification.visitRequestSuccess')))
      await queryClient.invalidateQueries(['listingVisitSlots', id])
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : t('verification.visitRequestFailed')
      toast.error(String(msg))
    }
  }

  const handleBookVisit = (slotId: number) => {
    if (!getAuthUserSubject(user)) {
      goToAuthForInterest()
      return
    }
    if (!canProceedAfterTenantVerification(identityVerification)) {
      setPendingVisitSlotId(slotId)
      setShowVerificationModal(true)
      return
    }
    void bookVisit(slotId)
  }

  const handleVerificationSubmit = async (
    files: Parameters<typeof submitTenantVerificationDocuments>[0]
  ) => {
    setIsSubmittingVerification(true)
    try {
      await submitTenantVerificationDocuments(files)
      await queryClient.invalidateQueries(['myVerification'])
      await queryClient.invalidateQueries(['applicationStatus', id])
      const updated = await loadMyVerification()
      setShowVerificationModal(false)

      if (!canProceedAfterTenantVerification(updated)) {
        toast.info(String(t('listing.interest.docsPendingMessage')))
        setPendingVisitSlotId(null)
        return
      }

      if (pendingVisitSlotId !== null) {
        await bookVisit(pendingVisitSlotId)
        setPendingVisitSlotId(null)
        return
      }

      toast.success(String(t('listing.interest.confirmedSuccess')))
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : t('verification.submitFailed')
      toast.error(String(msg))
    } finally {
      setIsSubmittingVerification(false)
    }
  }

  const handleCheckApplicationStatus = () => {
    navigate('/account')
  }

  const canRevealFullAddress =
    isOwner || (hasApplied && !isInterestOnStandby)

  const displayAddress = listing
    ? listingDisplayAddressLine(listing, canRevealFullAddress) ||
      listing.address?.city ||
      ''
    : ''

  const isVerified = listing?.verified === VerificationStatus.Approved
  const photos = listing?.images?.filter(Boolean) ?? []
  const openGallery = (startIndex: number) => {
    setGalleryIndex(startIndex)
    setGalleryOpen(true)
  }

  const listingTypeDisplay = listing ? listingTypeLabel(t, listing) : ''
  const unitTypeDisplay = listing ? listingUnitTypeLabel(t, listing) : null
  const roomsDisplay = listing?.bedrooms
    ? listingRoomLabel(t, listing.bedrooms)
    : null

  const parkingExtra = meta?.parking
  const parkingLines: string[] = []
  if (parkingExtra?.type) {
    parkingLines.push(
      t(`createAd.flow.parking.${parkingExtra.type}`, { defaultValue: parkingExtra.type })
    )
  }
  if (parkingExtra?.spaces) {
    parkingLines.push(
      appI18n.translate('listing.detail.parkingSpaces', { count: parkingExtra.spaces })
    )
  }
  if (parkingExtra?.evCharging) parkingLines.push(t('listing.detail.parkingEv'))
  if (parkingExtra?.bikeSpace) parkingLines.push(t('listing.detail.parkingBike'))
  if (parkingExtra?.storageLocker) parkingLines.push(t('listing.detail.parkingLocker'))
  const parkingMonthly = parkingExtra?.monthlyPrice
  if (parkingMonthly && String(parkingMonthly).trim() !== '') {
    const amount = Number.parseInt(String(parkingMonthly).replace(/\D/g, ''), 10)
    if (!Number.isNaN(amount) && amount > 0) {
      parkingLines.push(
        appI18n.translate('listing.detail.parkingPrice', {
          amount: amount.toLocaleString(moneyLocale),
        })
      )
    }
  }
  if (parkingExtra?.extraNote && String(parkingExtra.extraNote).trim()) {
    parkingLines.push(String(parkingExtra.extraNote).trim())
  }

  const descriptionText =
    typeof listing.description === 'string' && listing.description.trim()
      ? listing.description.trim()
      : null
  const descriptionNeedsTruncate =
    Boolean(descriptionText) &&
    (descriptionText?.length ?? 0) > DESCRIPTION_PREVIEW_LENGTH
  const descriptionPreview =
    descriptionNeedsTruncate && !descriptionExpanded && descriptionText
      ? `${descriptionText.slice(0, DESCRIPTION_PREVIEW_LENGTH).trim()}…`
      : descriptionText

  const parsedFloor = parseFloorFromApi(meta?.floor)
  const structuredTitle = listing
    ? buildStructuredListingTitle(t, listing)
    : t('listing.detail.untitled')
  const priceLabel = listing
    ? formatListingPrice(listing.price, listing.currency)
    : ''
  const availabilityLabel = listing
    ? formatListingAvailabilityLabel(
        t,
        listing.availability,
        i18n.language || 'fr-CA'
      )
    : ''
  const memberSinceLabel = listing?.createdAt
    ? formatLocalizedDate(listing.createdAt, lang, {
        month: 'long',
        year: 'numeric',
      })
    : ''

  const shareUrl =
    typeof window !== 'undefined'
      ? window.location.href
      : `https://wemovyou.web.app/listing/${id ?? ''}`

  const isFavorited = Boolean(listing?.isFavorited)
  const favoriteBusy = loadingFavoriteId === String(listing?.id)

  const statCards: { label: string; value: string }[] = []
  if (listingTypeDisplay) {
    statCards.push({
      label: t('listing.detail.listingType'),
      value: listingTypeDisplay,
    })
  }
  if (unitTypeDisplay) {
    statCards.push({
      label: t('listing.detail.unitType'),
      value: unitTypeDisplay,
    })
  }
  statCards.push({
    label: t('listing.detail.rooms'),
    value: roomsDisplay || '—',
  })
  statCards.push({
    label: t('listing.detail.bathrooms'),
    value: String(listing.bathrooms ?? '—'),
  })
  if (meta?.areaSqFt?.trim()) {
    statCards.push({
      label: t('listing.detail.area'),
      value: appI18n.translate('listing.detail.areaSqft', {
        value: meta.areaSqFt.trim(),
      }),
    })
  }
  if (parsedFloor.floor) {
    statCards.push({
      label: t('listing.detail.floor'),
      value: tFloorLabel(t, parsedFloor.floor, parsedFloor.floorCustom),
    })
  }
  if (canRevealFullAddress && listing.address.zipCode) {
    statCards.push({
      label: t('listing.detail.postalCode'),
      value: listing.address.zipCode,
    })
  }

  const heroTitle =
    typeof listing.title === 'string' && listing.title.trim()
      ? listing.title.trim()
      : structuredTitle
  const includesLine = includes.length ? includes.join(', ') : ''
  const bathsValue =
    listing.bathrooms !== null &&
    listing.bathrooms !== undefined &&
    String(listing.bathrooms).trim() !== ''
      ? String(listing.bathrooms)
      : ''
  const bathsPill = bathsValue
    ? `${bathsValue} ${t('createAd.flow.publication.preview.bathsSuffix')}`
    : null
  const roomsPill = roomsDisplay
    ? roomsDisplay.toLowerCase().includes('studio')
      ? roomsDisplay
      : `${roomsDisplay} ${t('createAd.flow.publication.preview.roomsSuffix')}`
    : null
  const petsPill =
    meta?.petsAllowed === 'no'
      ? t('createAd.flow.publication.preview.petsNo')
      : meta?.petsAllowed === 'conditions'
        ? t('createAd.flow.publication.preview.petsConditions')
        : meta?.petsAllowed === 'yes' || listing.details.petFriendly
          ? t('createAd.flow.publication.preview.petsYes')
          : null
  const hasVirtualTour =
    typeof meta?.virtualTour === 'string' && meta.virtualTour.trim() !== ''

  const secondaryStatCards = statCards.filter(
    card =>
      card.label === t('listing.detail.area') ||
      card.label === t('listing.detail.floor') ||
      card.label === t('listing.detail.postalCode')
  )

  const renderPrimaryCta = (className: string) => {
    // Own listings: no edit CTA on the public detail page — only share below.
    if (isOwner) return null
    if (needsVerificationAction) {
      return (
        <button
          type="button"
          onClick={handleContinueVerification}
          className={`${className} !bg-amber-600 hover:!bg-amber-700`}
        >
          {t('listing.interest.continueVerification')}
        </button>
      )
    }
    if (isAwaitingValidation) {
      return (
        <button
          type="button"
          onClick={handleCheckApplicationStatus}
          className={`${className} !bg-amber-100 !text-amber-900 ring-1 ring-amber-300 hover:!bg-amber-50`}
        >
          {t('listing.interest.awaitingValidation')}
        </button>
      )
    }
    if (hasApplied) {
      return (
        <button
          type="button"
          onClick={handleCheckApplicationStatus}
          className={`${className} !bg-green-600 hover:!bg-green-700`}
        >
          {t('listing.interest.viewStatus')}
        </button>
      )
    }
    return (
      <button type="button" onClick={handleExpressInterest} className={className}>
        {t('listing.interest.cta')}
      </button>
    )
  }

  const primaryCtaClass =
    'w-full rounded-full bg-[#0056D2] py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0046b3] hover:shadow-md active:scale-[0.99]'

  const reportListing = () => {
    const subject = encodeURIComponent(
      t('listing.detail.reportEmailSubject', { id: id ?? '' })
    )
    const body = encodeURIComponent(
      t('listing.detail.reportEmailBody', { url: shareUrl })
    )
    window.location.href = `mailto:mail@valterdamiani.shop?subject=${subject}&body=${body}`
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-slate-50 via-imovWhiteShadow to-white pb-24 lg:pb-0">
      <ListingPhotoGalleryModal
        photos={photos}
        initialIndex={galleryIndex}
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
      />
      <PropertySharePopup
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        shareUrl={shareUrl}
        anchorRef={shareAnchorRef}
      />

      <div className="mx-auto max-w-6xl px-4 py-4 md:py-8">
        <div className="mb-4 flex items-center justify-between gap-3 md:mb-6">
          <button
            type="button"
            onClick={() => navigate(listingReturnTo)}
            className="text-sm font-medium text-imovSecondary transition hover:text-imovPrimary hover:underline"
          >
            &larr; {t('listing.detail.backToListings')}
          </button>
          {isVerified ? (
            <ListingVerificationBadge verified={listing.verified} />
          ) : null}
        </div>

        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
          <div className="min-w-0 flex-1 space-y-6">
            <article className="rounded-2xl border border-gray-200 bg-white shadow-sm">
              {photos.length > 0 ? (
                <div className="p-2">
                  <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_120px] sm:grid-rows-2 md:grid-cols-[minmax(0,1fr)_140px]">
                    <PhotoTile
                      src={photos[0]}
                      onClick={() => openGallery(0)}
                      ariaLabel={t('listing.detail.openGallery')}
                      className="min-h-[200px] sm:row-span-2 sm:min-h-[260px]"
                    >
                      {listingTypeDisplay ? (
                        <span className="absolute left-3 top-3 rounded-md bg-imovPrimary px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow">
                          {listingTypeDisplay}
                        </span>
                      ) : null}
                    </PhotoTile>

                    {photos[1] ? (
                      <PhotoTile
                        src={photos[1]}
                        onClick={() => openGallery(1)}
                        className="hidden min-h-[124px] sm:block"
                        ariaLabel={t('listing.detail.openGallery')}
                      />
                    ) : (
                      <div className="hidden sm:block" aria-hidden />
                    )}

                    {photos[2] ? (
                      <PhotoTile
                        src={photos[2]}
                        onClick={() => openGallery(photos.length > 3 ? 0 : 2)}
                        className="hidden min-h-[124px] sm:block"
                        ariaLabel={t('listing.detail.openGallery')}
                      >
                        {photos.length > 3 ? (
                          <span className="absolute inset-0 flex flex-col items-center justify-center bg-black/55 text-center text-white transition group-hover:bg-black/60">
                            <span className="text-[10px] font-bold uppercase tracking-wider">
                              {t('createAd.flow.publication.preview.viewAll')}
                            </span>
                            <span className="text-sm font-semibold">
                              {photos.length}{' '}
                              {t('createAd.flow.publication.preview.photos')}
                            </span>
                          </span>
                        ) : null}
                      </PhotoTile>
                    ) : photos.length > 1 ? (
                      <div className="hidden sm:block" aria-hidden />
                    ) : null}
                  </div>

                  {photos.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => openGallery(0)}
                      className="mt-2 w-full rounded-xl border border-gray-200 py-2 text-center text-sm font-semibold text-imovPrimary sm:hidden"
                    >
                      {t('createAd.flow.publication.preview.viewAll')} ({photos.length})
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="flex aspect-[16/9] items-center justify-center bg-gray-100 text-sm text-gray-400">
                  {t('listing.detail.noPhotos')}
                </div>
              )}

              <div className="space-y-4 border-t border-gray-100 p-5 sm:p-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <h1 className="break-words text-xl font-bold leading-snug text-gray-900 sm:text-2xl">
                      {heroTitle}
                    </h1>
                    {displayAddress ? (
                      <p className="mt-2 flex items-start gap-1.5 text-sm text-gray-600">
                        <svg
                          className="mt-0.5 h-4 w-4 shrink-0 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        <span className="min-w-0 break-words [overflow-wrap:anywhere]">
                          {displayAddress}
                        </span>
                      </p>
                    ) : null}
                    {!canRevealFullAddress ? (
                      <p className="mt-1 text-xs text-gray-500">
                        {t('listing.detail.approximateAddressNote')}
                      </p>
                    ) : null}
                    {availabilityLabel ? (
                      <p className="mt-2 inline-flex max-w-full items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-imovPrimary">
                        <svg
                          className="h-3.5 w-3.5 shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        {availabilityLabel}
                      </p>
                    ) : null}
                  </div>
                  <div className="shrink-0 text-left sm:text-right">
                    <p className="break-words text-2xl font-bold text-gray-900 sm:text-3xl">
                      {priceLabel || '—'}
                    </p>
                    {includesLine ? (
                      <p className="mt-1 break-words text-[10px] font-semibold uppercase tracking-wide text-gray-500 sm:ml-auto sm:max-w-xs">
                        {t('createAd.flow.publication.preview.included')}: {includesLine}
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {unitTypeDisplay ? <FeaturePill>{unitTypeDisplay}</FeaturePill> : null}
                  {roomsPill ? <FeaturePill>{roomsPill}</FeaturePill> : null}
                  {bathsPill ? <FeaturePill>{bathsPill}</FeaturePill> : null}
                  {hasVirtualTour ? (
                    <FeaturePill>
                      {t('createAd.flow.publication.preview.virtualTour')}
                    </FeaturePill>
                  ) : null}
                  {petsPill ? <FeaturePill>{petsPill}</FeaturePill> : null}
                </div>
              </div>
            </article>

            {secondaryStatCards.length > 0 ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {secondaryStatCards.map(({ label, value }) => (
                  <StatCard key={label} label={label} value={value} />
                ))}
              </div>
            ) : null}

            <div className="rounded-2xl border border-blue-100 bg-blue-50/80 px-4 py-3 text-sm text-imovSecondary">
              <p className="mb-1 font-semibold text-imovPrimary">
                {t('listing.detail.whySimpleTitle')}
              </p>
              <p className="text-imovSecondary/80 [overflow-wrap:anywhere]">
                {t('listing.detail.whySimpleBody')}
              </p>
            </div>

            <div className="min-w-0 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <h2 className="mb-2 text-sm font-semibold text-imovSecondary">
                {t('createAd.flow.publication.preview.about')}
              </h2>
              {descriptionPreview ? (
                <>
                  <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-700 [overflow-wrap:anywhere]">
                    {descriptionPreview}
                  </p>
                  {descriptionNeedsTruncate ? (
                    <button
                      type="button"
                      onClick={() => setDescriptionExpanded(v => !v)}
                      className="mt-2 text-sm font-semibold text-imovPrimary hover:text-imovPrimaryDark"
                    >
                      {descriptionExpanded
                        ? t('createAd.flow.publication.preview.readLess')
                        : t('createAd.flow.publication.preview.readMore')}
                    </button>
                  ) : null}
                </>
              ) : (
                <p className="text-sm text-gray-500">
                  {t('listing.detail.descriptionFallback')}
                </p>
              )}
            </div>

            <div>
              <h2 className="mb-2 text-sm font-semibold text-imovSecondary">
                {t('listing.detail.inclusions')}
              </h2>
              {includes.length ? (
                <div className="flex flex-wrap gap-2">
                  {includes.map(item => (
                    <FeaturePill key={item}>{item}</FeaturePill>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500">{t('listing.detail.noInclusions')}</p>
              )}
            </div>

            {meta?.laundry ? (
              <div>
                <h2 className="mb-2 text-sm font-semibold text-imovSecondary">
                  {t('listing.detail.laundry')}
                </h2>
                <p className="text-sm text-imovSecondary/90">
                  {t(
                    `createAd.flow.laundry.${
                      meta.laundry === 'in_unit'
                        ? 'inUnit'
                        : meta.laundry === 'in_building'
                          ? 'inBuilding'
                          : 'none'
                    }`,
                    { defaultValue: meta.laundry }
                  )}
                </p>
              </div>
            ) : null}

            {buildingAmenityLabels.length > 0 ? (
              <div>
                <h2 className="mb-2 text-sm font-semibold text-imovSecondary">
                  {t('listing.detail.buildingAmenities')}
                </h2>
                <div className="flex flex-wrap gap-2">
                  {buildingAmenityLabels.map(item => (
                    <FeaturePill key={item}>{item}</FeaturePill>
                  ))}
                </div>
              </div>
            ) : null}

            {parkingLines.length > 0 ? (
              <div>
                <h2 className="mb-2 text-sm font-semibold text-imovSecondary">
                  {t('listing.detail.parking')}
                </h2>
                <ul className="list-inside list-disc space-y-1 break-words text-sm text-imovSecondary/90 [overflow-wrap:anywhere]">
                  {parkingLines.map(line => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div>
              <h2 className="mb-2 text-sm font-semibold text-imovSecondary">
                {t('listing.detail.conditions')}
              </h2>
              <div className="space-y-0 overflow-hidden rounded-2xl border border-gray-200 bg-white text-xs">
                <div className="flex flex-col gap-1 border-b border-gray-100 px-4 py-3">
                  <div className="flex items-center justify-between gap-4">
                    <span className="shrink-0 text-gray-500">
                      {t('listing.detail.petsLabel')}
                    </span>
                    <span className="text-right font-semibold text-imovSecondary [overflow-wrap:anywhere]">
                      {pets.line}
                    </span>
                  </div>
                  {pets.detail ? (
                    <p className="mt-1 leading-relaxed text-imovSecondary/90 [overflow-wrap:anywhere]">
                      {pets.detail}
                    </p>
                  ) : null}
                </div>
                {(
                  [
                    ['smoker', meta?.smokerAllowed],
                    ['pmr', meta?.pmrAccessible],
                    ['shortTerm', meta?.shortTermPossible],
                  ] as const
                ).map(([key, value]) => (
                  <div
                    key={key}
                    className="flex items-center justify-between gap-4 border-b border-gray-100 px-4 py-3"
                  >
                    <span className="text-gray-500">{t(`listing.detail.${key}`)}</span>
                    <span className="font-semibold text-imovSecondary">
                      {value === true
                        ? t('listing.detail.yes')
                        : value === false
                          ? t('listing.detail.no')
                          : '—'}
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-4 px-4 py-3">
                  <span className="text-gray-500">{t('listing.detail.furnished')}</span>
                  <span className="font-semibold text-imovSecondary">
                    {listing.details.furnished
                      ? t('listing.detail.yes')
                      : listing.details.semiFurnished
                        ? t('listing.detail.semiFurnished')
                        : t('listing.detail.no')}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 text-xs text-imovSecondary sm:grid-cols-2">
              <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
                <div className="mb-1 text-gray-500">{t('listing.detail.leaseEnd')}</div>
                <div className="font-semibold [overflow-wrap:anywhere]">
                  {listing.contractEnd
                    ? formatDate(listing.contractEnd)
                    : t('listing.detail.contractEndNotIndicated')}
                </div>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
                <div className="mb-1 text-gray-500">
                  {t('listing.detail.remainingTerm')}
                </div>
                <div className="font-semibold [overflow-wrap:anywhere]">
                  {listing.leaseTerm || t('listing.detail.remainingTbd')}
                </div>
              </div>
            </div>
          </div>

          <aside className="w-full shrink-0 lg:sticky lg:top-24 lg:w-80 lg:self-start">
            <div className="flex min-w-0 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white p-5 shadow-lg">
              <p className="break-words text-2xl font-bold text-imovSecondary [overflow-wrap:anywhere]">
                {priceLabel}
              </p>

              {!isOwner ? (
                <div className="mt-5">{renderPrimaryCta(primaryCtaClass)}</div>
              ) : null}

              {!isOwner && needsVerificationAction ? (
                <p className="mt-2 text-xs text-amber-700 [overflow-wrap:anywhere]">
                  {t('listing.interest.standbyHint')}
                </p>
              ) : null}
              {!isOwner && isAwaitingValidation ? (
                <p className="mt-2 text-xs text-amber-800 [overflow-wrap:anywhere]">
                  {t('listing.interest.awaitingValidationHint')}
                </p>
              ) : null}

              <div
                className={`grid gap-2 ${isOwner ? 'mt-5 grid-cols-1' : 'mt-3 grid-cols-2'}`}
              >
                {!isOwner ? (
                  <button
                    type="button"
                    disabled={favoriteBusy || !id}
                    onClick={() => id && void handleFavorite(id)}
                    className="rounded-full border border-gray-200 px-3 py-2.5 text-xs font-semibold text-imovSecondary transition hover:border-imovPrimary/40 hover:bg-imovPrimary/5 disabled:opacity-60"
                  >
                    {isFavorited
                      ? t('listing.detail.removeFavorite')
                      : t('listing.detail.addFavorite')}
                  </button>
                ) : null}
                <div ref={shareAnchorRef}>
                  <button
                    type="button"
                    onClick={() => setIsShareOpen(true)}
                    className="w-full rounded-full border border-gray-200 px-3 py-2.5 text-xs font-semibold text-imovSecondary transition hover:border-imovPrimary/40 hover:bg-imovPrimary/5"
                  >
                    {t('listing.detail.share')}
                  </button>
                </div>
              </div>

              {!isOwner && visitSlots.length > 0 ? (
                <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs font-semibold uppercase text-gray-500">
                    {t('verification.visitSlotsTitle')}
                  </p>
                  <ul className="mt-2 space-y-2">
                    {visitSlots.map(slot => (
                      <li key={slot.id}>
                        <button
                          type="button"
                          onClick={() => handleBookVisit(slot.id)}
                          disabled={
                            visitRequestMutation.isLoading || isSubmittingVerification
                          }
                          className="w-full rounded-lg border border-imovPrimary/30 bg-white px-3 py-2 text-left text-sm text-imovSecondary transition hover:bg-imovPrimary/5 disabled:opacity-60"
                        >
                          <span className="font-medium">
                            {formatDate(slot.start_at)}
                          </span>
                          <span className="ml-2 text-xs text-gray-500">
                            {t('verification.bookVisit')}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="mt-5 space-y-2 border-t border-gray-100 pt-4 text-xs text-imovSecondary">
                <p className="font-semibold">
                  {isVerified
                    ? t('listing.detail.advertiserVerified')
                    : t('listing.detail.advertiser')}
                </p>
                {memberSinceLabel ? (
                  <p className="text-gray-600">
                    {t('listing.detail.memberSince', { date: memberSinceLabel })}
                  </p>
                ) : null}
                <p className="text-gray-600">{t('listing.detail.respondsWithin')}</p>
              </div>
            </div>
          </aside>
        </div>

        {similarListings.length > 0 ? (
          <section className="mt-12 border-t border-gray-200 pt-10">
            <h2 className="mb-5 text-lg font-bold text-imovSecondary">
              {t('listing.detail.similarTitle')}
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {similarListings.map((property, index) => (
                <PropertyCard
                  key={property.id}
                  property={property}
                  currentIndex={null}
                  listIndex={index}
                  handleClick={() =>
                    navigate(`/listing/${property.id}`, {
                      state: { returnTo: listingReturnTo },
                    })
                  }
                />
              ))}
            </div>
          </section>
        ) : null}

        <div className="mt-10 flex flex-col gap-3 border-t border-gray-200 pt-8 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={() => navigate(listingReturnTo)}
            className="text-sm font-semibold text-imovSecondary transition hover:text-imovPrimary hover:underline"
          >
            &larr; {t('listing.detail.backToListings')}
          </button>
          <button
            type="button"
            onClick={reportListing}
            className="text-sm text-gray-500 underline-offset-2 transition hover:text-red-600 hover:underline"
          >
            {t('listing.detail.reportListing')}
          </button>
        </div>
      </div>

      {!isOwner ? (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 p-3 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] backdrop-blur lg:hidden">
          {renderPrimaryCta(primaryCtaClass)}
        </div>
      ) : null}

      <InterestModal
        isOpen={showInterestModal}
        isLoading={isSubmittingInterest}
        onClose={() => setShowInterestModal(false)}
        onSubmit={handleInterestSubmit}
      />

      <SecurityVerificationModal
        isOpen={showVerificationModal}
        mode="tenant"
        isLoading={isSubmittingVerification}
        onClose={() => {
          setShowVerificationModal(false)
          setPendingVisitSlotId(null)
        }}
        onSubmit={files => void handleVerificationSubmit(files)}
      />
    </div>
  )
}

export default PropertyPage
